import { EMOJI_STATIC_PATH_V2 } from "@/api/staticPaths";
import { canv } from "@/canv/global";
import { isSafari } from "@/utils";
import {
    CENTER_Y_OFFSET,
    EMOJI_FONT_FAMILY,
    FONT_FALLBACKS,
    FONT_SIZE_MULTIPLIER,
} from "./constants";
import type { EmojiCache } from "./types";

const emojiCache: EmojiCache = new Map();

let emojiFontInitCompleted = false;

const buildFontLoadErrorString = (err, fontUrl) => {
    const name = err?.name ?? "UnknownError";
    const message = err?.message ?? String(err);

    const descriptions: Record<string, string> = {
        NetworkError: "Сеть недоступна, URL не найден (404), либо CORS блокировка.",
        SyntaxError: "Неверный формат шрифта или битые данные. Также может быть неверный URL.",
        InvalidStateError: "Шрифт находится в некорректном состоянии или его нельзя использовать.",
        AbortError: "Загрузка была принудительно прервана браузером или процессом.",
        UnknownError: "Произошла нераспознанная ошибка, не соответствующая стандартным типам.",
    };
    const description = descriptions[name] ?? "Неизвестная ошибка загрузки шрифта.";

    return (
        `Ошибка загрузки Emoji шрифта\n` +
        `Тип: ${name}\n` +
        `Сообщение: ${message}\n` +
        `Описание: ${description}\n` +
        `URL: ${fontUrl}\n`
    );
};

/**
 * Инициализирует загрузку Emoji шрифта
 * Должна быть вызвана после инициализации canv.host
 * Для Safari не загружает Noto Color Emoji, использует системные шрифты
 */
export const initEmojiFont = (): void => {
    if (emojiFontInitCompleted) return;

    if (isSafari()) {
        emojiFontInitCompleted = true;
        return;
    }

    const fontUrl = `${canv.host}${EMOJI_STATIC_PATH_V2}emoji.woff2`;

    const font = new FontFace(EMOJI_FONT_FAMILY, `url(${fontUrl})`);

    font.load()
        .then((loadedFont) => {
            document.fonts.add(loadedFont);
        })
        .catch((err) => {
            canv.captureException(new Error(buildFontLoadErrorString(err, fontUrl)));
        })
        .finally(() => {
            emojiFontInitCompleted = true;
            canv.redraw();
        });
};

/**
 * Формирует строку font-family для эмодзи
 */
const getEmojiFontFamily = (): string => {
    return `'${EMOJI_FONT_FAMILY}', ${FONT_FALLBACKS.map((f) => `'${f}'`).join(", ")}`;
};

/**
 * Отрисовывает эмодзи на canvas контексте
 */
const drawEmoji = (
    ctx: CanvasRenderingContext2D,
    emoji: string,
    x: number,
    y: number,
    width: number,
    height: number,
): void => {
    ctx.save();

    const fontSize = Math.max(width, height) * FONT_SIZE_MULTIPLIER;
    ctx.font = `${fontSize}px ${getEmojiFontFamily()}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const centerX = x + width / 2;
    const centerY = y + height * CENTER_Y_OFFSET;

    ctx.fillText(emoji, centerX, centerY);
    ctx.restore();
};

/**
 * Создает canvas контекст
 */
const createCanvasContext = (width: number, height: number): CanvasRenderingContext2D => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("Failed to get 2d context for emoji cache");
    }

    return ctx;
};

/**
 * Получает или создает кэш для эмодзи
 */
const getOrCreateEmojiCache = (emoji: string) => {
    if (!emojiCache.has(emoji)) {
        emojiCache.set(emoji, new Map());
    }
    return emojiCache.get(emoji);
};

/**
 * Создает ключ для кэша на основе размеров
 */
const createSizeKey = (width: number, height: number): string => {
    return `${width}x${height}`;
};

/**
 * Определяет масштаб для отрисовки эмодзи
 * Для маленьких эмодзи используем upscaling для улучшения качества
 * Для больших эмодзи используем downscaling для ухудшения качества
 *
 * @param width - ширина эмодзи
 * @param height - высота эмодзи
 */
const getScale = (width: number, height: number) => {
    const minSize = Math.min(width, height);

    /** При корректировках не забывать тестировать экспорт в высоком качестве на размер файла и скорость! */
    if (minSize < 150) return 5;

    if (minSize < 500) return 4.5;

    if (minSize < 700) return 1.5;

    if (minSize < 1000) return 0.7;

    if (minSize < 1200) return 0.6;

    if (minSize < 2000) return 0.4;

    if (minSize < 3000) return 0.3;

    if (minSize < 5000) return 0.2;

    return 0.1;
};

/**
 * Получает кэшированный canvas для эмодзи или создает новый
 * Для маленьких эмодзи использует upscaling для улучшения качества
 * Для больших эмодзи использует downscaling для ухудшения качества
 *
 * @param emoji - строка эмодзи
 * @param width - ширина эмодзи
 * @param height - высота эмодзи
 * @returns HTMLCanvasElement с отрисованным эмодзи
 */
const getCachedEmojiCanvas = (emoji: string, width: number, height: number): HTMLCanvasElement => {
    const sizeKey = createSizeKey(width, height);
    const sizeCache = getOrCreateEmojiCache(emoji);

    // Проверяем наличие кэша
    const cached = sizeCache?.get(sizeKey);
    if (cached) return cached;

    // Определяем масштаб для улучшения качества маленьких эмодзи или ухудшения больших
    const scale = getScale(width, height);
    const canvasWidth = Math.ceil(width * scale);
    const canvasHeight = Math.ceil(height * scale);

    // Создаем canvas с учетом масштаба
    const ctx = createCanvasContext(canvasWidth, canvasHeight);
    const canvas = ctx.canvas;

    // Отрисовываем эмодзи в увеличенном масштабе (для маленьких) или уменьшенном (для больших)
    drawEmoji(ctx, emoji, 0, 0, canvasWidth, canvasHeight);

    // Сохраняем в кэш
    sizeCache?.set(sizeKey, canvas);

    return canvas;
};

/**
 * Отрисовывает эмодзи на canvas,
 * использует кэширование для оптимизации производительности
 */
export const renderEmojiToCanvas = (
    emoji: string,
    x: number,
    y: number,
    width: number,
    height: number,
) => {
    // Рисуем только если попытка инициализации шрифта завершена
    // (шрифт загружен успешно или используется fallback)
    if (!emojiFontInitCompleted) return;

    // Используем кэш для оптимизации производительности
    const cachedCanvas = getCachedEmojiCanvas(emoji, width, height);
    canv.ctx.drawImage(cachedCanvas, x, y, width, height);
};
